import { isResource, resolveId } from '@medplum/core'
import type {
  Attachment,
  CodeableConcept,
  Coding,
  Coverage,
  DiagnosticReport,
  DocumentReference,
  ExtractResource,
  Extension as FHIRExtension,
  Identifier,
  Organization,
  Patient,
  Practitioner,
  Reference,
  Resource,
  ResourceType,
  ServiceRequest,
} from '@medplum/fhirtypes'
import { formatDistance, isBefore } from 'date-fns'
import { compact, omit, orderBy, uniqBy } from 'lodash-es'
import {
  AI_ATTEMPT_LIMIT,
  hasAttemptBudget,
  nextFailedAttemptCount,
  parseFailedAttemptCount,
} from '#src/aiAttemptBudget.ts'
import { setAttachmentConversionState } from '#src/attachmentConversion.ts'
import {
  codeableConcept,
  codeFromCodeableConcept,
  hasCodeableConcept,
  systemFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  ATTACHMENT_CONTENT_HASH_SYSTEM,
  DOCUMENT_CATEGORY_SYSTEM,
  DOCUMENT_FINGERPRINT_SYSTEM,
  DOCUMENT_INTAKE_MANAGED,
  DOCUMENT_INTAKE_SYSTEM,
  DOCUMENT_LAB_COPY_GROUP_SYSTEM,
  DOCUMENT_LAB_COPY_FILED_SYSTEM,
  DOCUMENT_LAB_COPY_OF_SYSTEM,
  DOCUMENT_LAB_COPY_PARTICIPANT_SYSTEM,
  DOCUMENT_LAB_COPY_PINNED_SYSTEM,
  DOCUMENT_REFERENCE_AI_SUMMARY_SYSTEM,
  DOCUMENT_REFERENCE_CONTAINS_HANDWRITING_SYSTEM,
  DOCUMENT_REFERENCE_INLINE_PASTE_PROCESSING_SYSTEM,
  DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM,
  DOCUMENT_REFERENCE_PREDICTED_RELATIONS_EXTENSION,
  DOCUMENT_REFERENCE_PREDICTED_RELATIONS_FAILED_CODE,
  DOCUMENT_REFERENCE_PREDICTED_RELATIONS_NONE_CODE,
  DOCUMENT_REFERENCE_TRANSCRIPTION_CONFIDENCE_SYSTEM,
  DOCUMENT_REFERENCE_TRANSCRIPTION_PAGES_SYSTEM,
  DOCUMENT_REFERENCE_TRANSCRIPTION_SYSTEM,
  DOCUMENT_SOURCE_SYSTEM,
  DOCUMENT_REFERENCE_PREDICTION_FAILED_ATTEMPTS_SYSTEM,
  DOCUMENT_REFERENCE_PREDICTION_STARTED_SYSTEM,
  DOCUMENT_TRANSCRIPTION_FAILED_ATTEMPTS_SYSTEM,
  DOCUMENT_TRANSCRIPTION_OUTCOME_SYSTEM,
  DOCUMENT_TRANSCRIPTION_STARTED_SYSTEM,
  DOCUMENT_TRANSCRIPTION_REQUEST_SYSTEM,
  DOCUMENT_VISIBILITY_SYSTEM,
  DOCUMENT_VISIBILITY_SYSTEM_INTERNAL,
  ELATION_SANDBOX_ID_SYSTEM,
  EXTERNAL_FILE_ID_SYSTEM,
  QUEST_ACCESSION_IDENTIFIER_SYSTEM,
} from '#src/codeSystems.ts'
import {
  HEALTH_GORILLA_DOCUMENT_TYPE_SYSTEM,
  HEALTH_GORILLA_IDENTIFIER_SYSTEM,
  HEALTH_GORILLA_META_SOURCE,
} from '#src/constants.ts'
import { type DateString, toDateString } from '#src/dates.ts'
import { Extension } from '#src/decorators.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { invariant } from '#src/invariant.ts'
import type { JsonLike, JsonObject } from '#src/json.ts'
import {
  APPLICATION_DOCUMENT_TYPES,
  DOCUMENT_CATEGORIES,
  DOCUMENT_SOURCES,
  DOCUMENT_TYPES,
  DocumentCategory,
  DocumentSource,
  type DocumentSourceCode,
  DocumentTranscriptionOutcome,
  DocumentTypes,
  InlinePasteProcessingStatus,
  type InlinePasteProcessingStatus as InlinePasteProcessingStatusCode,
  INTERNAL_DOCUMENT_CATEGORY_GRANERX_HL7,
  normalizeDocumentCategories,
} from '#src/models/document-references/constants.ts'
import {
  BEHAVIORAL_HEALTH_FOLDER_ID,
  FOLDERS,
  type Folder,
} from '#src/models/document-references/folder.ts'
import {
  LabCopyKind,
  type LabCopyState,
  parseQuestAccession,
} from '#src/models/document-references/labCopies.ts'
import type { Order } from '#src/models/orders/index.ts'
import { getReferenceString } from '#src/references.ts'
import type { QueryTypes } from '#src/search.ts'
import {
  DOCUMENT_REFERENCE_ATTACHMENT_MAGIC_LINK_EXTENSION,
  FOLDER_ID_SYSTEM,
} from '#src/structureDefinitions.ts'
import {
  build,
  isNonNegativeInteger,
  isOfType,
  now,
  parseReferenceString,
} from '#src/utils.ts'

export interface AttachmentWithId extends Attachment {
  id: string
}

export interface DocumentTranscriptionPageSpan {
  attachmentIndex: number
  page: number
  start: number
  end: number
}

function transcriptionPageSpansFrom(
  value: JsonLike | undefined
): DocumentTranscriptionPageSpan[] | undefined {
  if (!Array.isArray(value) || value.length === 0) {
    return undefined
  }

  const spans: DocumentTranscriptionPageSpan[] = []
  for (const candidate of value) {
    if (!candidate || typeof candidate !== 'object') {
      return undefined
    }
    const record = candidate as JsonObject
    if (
      !isNonNegativeInteger(record.attachmentIndex) ||
      !isNonNegativeInteger(record.page) ||
      record.page === 0 ||
      !isNonNegativeInteger(record.start) ||
      !isNonNegativeInteger(record.end) ||
      record.end < record.start
    ) {
      return undefined
    }

    const previous = spans.at(-1)
    const expectedPage =
      !previous || record.attachmentIndex !== previous.attachmentIndex
        ? 1
        : previous.page + 1
    if (
      record.page !== expectedPage ||
      (previous &&
        (record.attachmentIndex < previous.attachmentIndex ||
          record.start < previous.end))
    ) {
      return undefined
    }

    spans.push({
      attachmentIndex: record.attachmentIndex,
      page: record.page,
      start: record.start,
      end: record.end,
    })
  }
  return spans
}

const DOCUMENT_SOURCE_LOOKUP = Object.values(DOCUMENT_SOURCES).reduce(
  (acc, coding) => {
    acc[coding.code] = coding
    return acc
  },
  {} as Record<
    DocumentSourceCode,
    (typeof DOCUMENT_SOURCES)[keyof typeof DOCUMENT_SOURCES]
  >
)

/**
 * HealthGorilla integration bots persist their internal request/response payloads
 * as `DocumentReference`s whose only distinguishing field is this `category.text`
 * (no `category.coding`, no `type`, no visibility tag). They are debug artifacts
 * excluded centrally in `searchQuery()` so every `by*` query inherits the exclusion.
 * See FB-732.
 */
const HG_DEBUG_LOG_CATEGORY_TEXTS = [
  'HG Debug Log - send-to-health-gorilla',
  'HG Debug Log - receive-from-health-gorilla',
]

export function buildDocumentReference(
  docRef: Omit<DocumentReference, 'resourceType' | 'date'>
): DocumentReference {
  return build('DocumentReference', {
    ...docRef,
    date: now(),
  })
}

export type ExtendedDocumentReferenceParams = Omit<
  DocumentReference,
  'resourceType' | 'date' | 'category' | 'status' | 'content' | 'author'
> & {
  categories?: DocumentCategory[]
  attachments?: Attachment[]
  author: Reference<Practitioner | Organization>
}

interface PredictedRelationData {
  confidence: number
  dismissed?: boolean
  reason?: string
}

const HEALTH_GORILLA_REPORT_ID = /^[0-9a-f]{24}$/i
const HEALTH_GORILLA_PDF_ID = /^[0-9a-f]{24}-.+\.pdf$/i
const HEALTH_GORILLA_DOCUMENT_ID = /^([0-9a-f]{24})(?:-.+\.pdf)?$/i

export class ExtendedDocumentReference extends ExtendedResource<DocumentReference> {
  static readonly RESOURCE_TYPE = 'DocumentReference'

  static isInstance(value: unknown): value is DocumentReference {
    return isResource(value) && isOfType(value, 'DocumentReference')
  }

  static defaultSearchQuery(): QueryTypes & { 'type:not': CodeableConcept[] } {
    // Application documents belong to their own surfaces, not clinical document lists.
    return {
      'type:not': APPLICATION_DOCUMENT_TYPES.map(
        (type) => DOCUMENT_TYPES[type]
      ),
    }
  }

  static isApplicationDocument(resource: Resource): boolean {
    return (
      resource.resourceType === 'DocumentReference' &&
      APPLICATION_DOCUMENT_TYPES.some((type) =>
        hasCodeableConcept(resource.type, DOCUMENT_TYPES[type])
      )
    )
  }

  static searchQuery(): QueryTypes {
    return {
      /* `_id` breaks ties. `_lastUpdated` is not unique — bulk imports stamp
       * hundreds of documents with the same instant — and a participant with
       * more documents than `_count` is read by paging through offsets. With
       * only a non-unique key, a page boundary that lands inside a tied group
       * orders that group differently per request, so a document can come back
       * on two pages while another is never returned at all. */
      _sort: '-_lastUpdated,_id',
      _count: 1000,
      'status:not': 'entered-in-error',
      ...this.defaultSearchQuery(),
      // exclude HealthGorilla debug logs (FB-732) and GraneRx raw HL7 audit records
      'category:not': [
        ...HG_DEBUG_LOG_CATEGORY_TEXTS,
        `${INTERNAL_DOCUMENT_CATEGORY_GRANERX_HL7.system}|${INTERNAL_DOCUMENT_CATEGORY_GRANERX_HL7.code}`,
      ],
      '_tag:not': [
        `${DOCUMENT_VISIBILITY_SYSTEM}|${DOCUMENT_VISIBILITY_SYSTEM_INTERNAL}`,
        `${DOCUMENT_SOURCE_SYSTEM}|${DocumentSource.AI_CHAT}`,
      ],
    }
  }

  static byAuthor(author: Reference<Practitioner | Organization>): QueryTypes {
    return {
      ...this.searchQuery(),
      author,
    }
  }

  static byParticipant(params: {
    participant?: Reference<Patient>
  }): QueryTypes {
    return {
      ...this.searchQuery(),
      patient: params.participant,
    }
  }

  static byParticipantExcludingFolders(
    participant: Reference<Patient>
  ): QueryTypes {
    return {
      ...this.byParticipant({ participant }),
      '_tag:not': [
        `${DOCUMENT_VISIBILITY_SYSTEM}|${DOCUMENT_VISIBILITY_SYSTEM_INTERNAL}`,
        `${DOCUMENT_SOURCE_SYSTEM}|${DocumentSource.AI_CHAT}`,
        { system: FOLDER_ID_SYSTEM },
      ],
    }
  }

  static byParticipantInFolder(
    participant: Reference<Patient>,
    folderId: string
  ): QueryTypes {
    return {
      ...this.byParticipant({ participant }),
      _tag: { system: FOLDER_ID_SYSTEM, code: folderId },
    }
  }

  static byParticipantInBehavioralHealth(
    participant: Reference<Patient>
  ): QueryTypes {
    return this.byParticipantInFolder(participant, BEHAVIORAL_HEALTH_FOLDER_ID)
  }

  static byRelated(related: Reference): QueryTypes {
    return {
      ...this.searchQuery(),
      related,
    }
  }

  /**
   * Documents AI-predicted to relate to an order, tagged with the order's
   * reference string. Spreads `searchQuery()` so it inherits the same exclusions
   * as every other `by*` query.
   */
  static byPredictedRelation(orderReferenceString: string): QueryTypes {
    return {
      ...this.searchQuery(),
      _tag: `${DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM}|${orderReferenceString}`,
      _count: 100,
    }
  }

  // Every copy that carries the group tag for this accession, shown or not.
  static byLabCopyGroup(
    participant: Reference<Patient>,
    accession: string
  ): QueryTypes {
    return {
      ...this.byParticipant({ participant }),
      _tag: `${DOCUMENT_LAB_COPY_GROUP_SYSTEM}|${accession}`,
    }
  }

  // Elation copies carry the accessions of their report as identifiers.
  static byQuestAccession(
    participant: Reference<Patient>,
    accession: string
  ): QueryTypes {
    return {
      ...this.byParticipant({ participant }),
      identifier: `${QUEST_ACCESSION_IDENTIFIER_SYSTEM}|${accession}`,
    }
  }

  // The report-rendering document Health Gorilla delivers carries the report's
  // Health Gorilla id as its whole identifier value.
  static byHealthGorillaId(healthGorillaId: string): QueryTypes {
    return {
      ...this.searchQuery(),
      identifier: `${HEALTH_GORILLA_IDENTIFIER_SYSTEM}|${healthGorillaId}`,
    }
  }

  // Health Gorilla cites the documents a result came from on its leaf
  // Observations (`derivedFrom`), which a report reaches through its panels.
  // Two shapes occur, with a panel layer or without, so both are searched. This
  // reaches documents that carry no participant, which no patient search can.
  static byDiagnosticReportSources(
    report: Reference<DiagnosticReport>
  ): QueryTypes[] {
    const id = resolveId(report)
    return [
      {
        ...this.searchQuery(),
        '_has:Observation:derived-from:_has:Observation:has-member:_has:DiagnosticReport:result:_id':
          id,
      },
      {
        ...this.searchQuery(),
        '_has:Observation:derived-from:_has:DiagnosticReport:result:_id': id,
      },
    ]
  }

  // Every Health Gorilla document filed to a participant. A Quest PDF's
  // identifier is `<report id>-<file name>`, which a token search cannot match by
  // prefix, so reconcile reads the participant's Health Gorilla documents and
  // matches the report id in code. This finds a PDF no Observation cites.
  static byHealthGorillaParticipant(
    participant: Reference<Patient>
  ): QueryTypes {
    return {
      ...this.byParticipant({ participant }),
      identifier: `${HEALTH_GORILLA_IDENTIFIER_SYSTEM}|`,
    }
  }

  // The copies of this group that were voided. A voided primary is how the
  // rest learn to re-home, so reconcile reads them too, to clear their marks.
  static byLabCopyGroupVoided(
    participant: Reference<Patient>,
    accession: string
  ): QueryTypes {
    return {
      ...omit(this.byLabCopyGroup(participant, accession), 'status:not'),
      status: 'entered-in-error',
    }
  }

  // Every document that carries a group tag, whatever its age or status: shown,
  // hidden, pinned, filed, or voided. The sweep reconciles each one's group, so a
  // dropped event of any kind is repaired however long ago it happened. Sorted
  // by `_lastUpdated` then `_id` so a pager can walk it without skipping ties.
  static labCopiesInAnyGroup(): QueryTypes {
    return {
      ...omit(this.searchQuery(), 'status:not'),
      _tag: `${DOCUMENT_LAB_COPY_GROUP_SYSTEM}|`,
      _sort: '_lastUpdated,_id',
    }
  }

  // Everything a lab copy sweep reads: Health Gorilla's documents (which carry
  // no accession themselves) and Elation documents that saved one.
  static labCopyCandidates(): QueryTypes[] {
    return [
      HEALTH_GORILLA_IDENTIFIER_SYSTEM,
      QUEST_ACCESSION_IDENTIFIER_SYSTEM,
    ].map((system) => ({
      ...this.searchQuery(),
      identifier: `${system}|`,
      _sort: '_lastUpdated,_id',
    }))
  }

  // With lab copy grouping off, a Quest PDF the reconciler filed to this
  // participant stays out, so the view is the one that existed before.
  static excludingLabCopyFiled(query: QueryTypes): QueryTypes {
    return this.withExcludedTag(query, DOCUMENT_LAB_COPY_FILED_SYSTEM)
  }

  // The Documents table shows one row per result: a copy marked as a copy of
  // another is left out. A copy with no marker, including every copy while
  // grouping is off, is always returned.
  static excludingLabCopySecondaries(query: QueryTypes): QueryTypes {
    return this.withExcludedTag(query, DOCUMENT_LAB_COPY_OF_SYSTEM)
  }

  // Leaves out the documents filed in any of these folders. Callers pass the
  // folders the reader has no role for, so the search never returns what the
  // reader may not see, rather than the UI hiding it afterwards.
  static excludingFolders(query: QueryTypes, folderIds: string[]): QueryTypes {
    if (folderIds.length === 0) {
      return query
    }
    const excluded = query['_tag:not']
    return {
      ...query,
      '_tag:not': [
        ...(Array.isArray(excluded) ? excluded : compact([excluded])),
        ...folderIds.map((code) => ({ system: FOLDER_ID_SYSTEM, code })),
      ],
    }
  }

  private static withExcludedTag(
    query: QueryTypes,
    system: string
  ): QueryTypes {
    const excluded = query['_tag:not']
    return {
      ...query,
      '_tag:not': [
        ...(Array.isArray(excluded) ? excluded : compact([excluded])),
        { system },
      ],
    }
  }

  // Current documents whose transcription failed with retry budget left.
  // `failed` stays the outcome selector; the failed-attempt tag is what tells a
  // first failure from a final one.
  static byRetryableFailedTranscription(): QueryTypes {
    return {
      ...this.defaultSearchQuery(),
      status: 'current',
      _tag: `${DOCUMENT_TRANSCRIPTION_OUTCOME_SYSTEM}|${DocumentTranscriptionOutcome.FAILED}`,
      '_tag:not': `${DOCUMENT_TRANSCRIPTION_FAILED_ATTEMPTS_SYSTEM}|${AI_ATTEMPT_LIMIT}`,
    }
  }

  // Unfiled current documents whose relation prediction failed with retry
  // budget left. A filed document needs no prediction.
  static byRetryableFailedPrediction(): QueryTypes {
    return {
      ...this.defaultSearchQuery(),
      status: 'current',
      'subject:missing': 'true',
      _tag: `${DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM}|${DOCUMENT_REFERENCE_PREDICTED_RELATIONS_FAILED_CODE}`,
      '_tag:not': `${DOCUMENT_REFERENCE_PREDICTION_FAILED_ATTEMPTS_SYSTEM}|${AI_ATTEMPT_LIMIT}`,
    }
  }

  static byOrderPDF(order: Order): QueryTypes {
    return {
      ...this.byRelated(order.reference),
      type: DOCUMENT_TYPES[DocumentTypes.OrderPDF],
    }
  }

  static byCoverage({
    participant,
    coverage,
  }: {
    participant: Reference<Patient>
    coverage?: Reference<Coverage>
  }): QueryTypes {
    return {
      ...this.searchQuery(),
      'related:Coverage.patient': participant,
      related: coverage,
    }
  }

  constructor({
    categories,
    attachments,
    author,
    ...rest
  }: ExtendedDocumentReferenceParams) {
    super({
      status: 'current',
      date: now(),
      content: [],
      author: [author],
      docStatus: 'preliminary',
      ...rest,
    })

    if (categories) {
      this.categories = categories
    }

    if (attachments) {
      this.attachments = attachments
    }
  }

  /**
   * Builds a `DocumentReference` for a file uploaded through the AI chat.
   *
   * The result is stamped with `source = AI_CHAT`; `searchQuery()` excludes
   * that source so chat uploads stay out of every `by*` patient query.
   * Centralizing the provenance here means callers cannot accidentally create
   * a visible chat upload.
   */
  static forAiChatUpload({
    subject,
    author,
    attachments,
  }: {
    subject: Reference<Patient>
    author: Reference<Practitioner | Organization>
    attachments: Attachment[]
  }): ExtendedDocumentReference {
    const docRef = new ExtendedDocumentReference({
      subject,
      author,
      attachments,
    })
    docRef.source = DocumentSource.AI_CHAT
    return docRef
  }

  get source(): DocumentSourceCode | undefined {
    const tag = this.tags.find((t) => t.system === DOCUMENT_SOURCE_SYSTEM)
    return tag?.code as DocumentSourceCode | undefined
  }

  set source(source: DocumentSourceCode | undefined) {
    if (!source) {
      this.setTagsBySystem(DOCUMENT_SOURCE_SYSTEM, [])
      return
    }
    const coding = DOCUMENT_SOURCE_LOOKUP[source]
    this.setTagsBySystem(DOCUMENT_SOURCE_SYSTEM, [coding])
  }

  get sourceDisplay(): string {
    const source = this.source
    if (!source) {
      return ''
    }
    return DOCUMENT_SOURCE_LOOKUP[source]?.display ?? source
  }

  /**
   * Asks the transcription subscription to run on this document without
   * changing its content. The subscription only fires on a metadata update when
   * this tag's code changes, so `requestId` must differ from any earlier request
   * (a script name plus run timestamp). It also clears the transcription
   * outcome, which would otherwise make the handler skip the document.
   * A deliberate request also forgets earlier failed attempts so the document
   * gets a fresh budget; the automatic retry sweep passes `keepFailedAttempts`
   * so the retry that fails again is final. A failure recorded before attempts
   * were counted has no count, so the kept count is at least 1.
   */
  requestTranscription(
    requestId: string,
    options: { keepFailedAttempts?: boolean } = {}
  ): void {
    this.transcriptionOutcome = undefined
    this.transcriptionStartedAt = undefined
    this.transcriptionFailedAttempts = options.keepFailedAttempts
      ? (this.transcriptionFailedAttempts ?? 1)
      : undefined
    this.setTag(DOCUMENT_TRANSCRIPTION_REQUEST_SYSTEM, requestId)
  }

  get transcriptionFailedAttempts(): number | undefined {
    return parseFailedAttemptCount(
      this.getTag(DOCUMENT_TRANSCRIPTION_FAILED_ATTEMPTS_SYSTEM)?.code
    )
  }

  set transcriptionFailedAttempts(count: number | undefined) {
    this.setTagsBySystem(
      DOCUMENT_TRANSCRIPTION_FAILED_ATTEMPTS_SYSTEM,
      count ? [{ code: String(count) }] : []
    )
  }

  // Records a failed outcome and counts it against the attempt budget.
  markTranscriptionFailed(): void {
    this.transcriptionFailedAttempts = nextFailedAttemptCount(
      this.transcriptionFailedAttempts
    )
    this.transcriptionOutcome = DocumentTranscriptionOutcome.FAILED
  }

  get hasTranscriptionAttemptBudget(): boolean {
    return hasAttemptBudget(this.transcriptionFailedAttempts)
  }

  get transcriptionStartedAt(): string | undefined {
    return this.getTag(DOCUMENT_TRANSCRIPTION_STARTED_SYSTEM)?.code
  }

  set transcriptionStartedAt(startedAt: string | undefined) {
    this.setTagsBySystem(
      DOCUMENT_TRANSCRIPTION_STARTED_SYSTEM,
      startedAt ? [{ code: startedAt }] : []
    )
  }

  get transcriptionOutcome(): DocumentTranscriptionOutcome | undefined {
    const code = this.getTag(DOCUMENT_TRANSCRIPTION_OUTCOME_SYSTEM)?.code
    if (
      code === DocumentTranscriptionOutcome.FAILED ||
      code === DocumentTranscriptionOutcome.NO_TEXT
    ) {
      return code
    }
    return undefined
  }

  set transcriptionOutcome(outcome: DocumentTranscriptionOutcome | undefined) {
    this.setTagsBySystem(
      DOCUMENT_TRANSCRIPTION_OUTCOME_SYSTEM,
      outcome ? [{ code: outcome }] : []
    )
  }

  get inlinePasteProcessingStatus():
    | InlinePasteProcessingStatusCode
    | undefined {
    const code = this.getTag(
      DOCUMENT_REFERENCE_INLINE_PASTE_PROCESSING_SYSTEM
    )?.code
    if (
      code === InlinePasteProcessingStatus.PENDING ||
      code === InlinePasteProcessingStatus.COMPLETE ||
      code === InlinePasteProcessingStatus.NO_TEXT
    ) {
      return code
    }
    return undefined
  }

  set inlinePasteProcessingStatus(
    status: InlinePasteProcessingStatusCode | undefined
  ) {
    this.setTagsBySystem(
      DOCUMENT_REFERENCE_INLINE_PASTE_PROCESSING_SYSTEM,
      status ? [{ code: status }] : []
    )
  }

  get title(): string {
    const description = this.resource.description?.trim()
    if (description) {
      return description
    }
    if (this.attachments.length > 1) {
      return `${this.attachments.length} Attachments`
    }
    if (this.attachments.length === 1) {
      return this.attachments[0].title ?? 'Attachment'
    }
    return ''
  }

  /* Display-ready title for any list, badge, or activity entry that
   * surfaces a document reference. Prefer this over composing
   * `title || 'Untitled document'` at the call site. */
  get displayTitle(): string {
    return this.title.trim() || 'Untitled document'
  }

  get description(): string {
    return this.resource.description ?? ''
  }

  set description(description: string) {
    this.resource.description = description
  }

  get categories(): DocumentCategory[] {
    return (
      this.resource.category
        ?.filter(
          (c) => systemFromCodeableConcept(c) === DOCUMENT_CATEGORY_SYSTEM
        )
        .map((c) => codeFromCodeableConcept(c) as DocumentCategory) ?? []
    )
  }

  set categories(categories: DocumentCategory[]) {
    const nonDocumentCategories =
      this.resource.category?.filter(
        (c) => systemFromCodeableConcept(c) !== DOCUMENT_CATEGORY_SYSTEM
      ) ?? []

    const documentCategories = categories.map((category) => {
      const categoryInfo = DOCUMENT_CATEGORIES[category]
      if (categoryInfo) {
        return codeableConcept(categoryInfo)
      }
      // Handle unknown/deprecated categories - preserve with minimal coding
      return codeableConcept({
        system: DOCUMENT_CATEGORY_SYSTEM,
        code: category,
      })
    })

    this.resource.category = [...nonDocumentCategories, ...documentCategories]

    if (
      (this.typeCode === DocumentTypes.MEDICAID_ID ||
        this.typeCode === DocumentTypes.MEDICARE_ID) &&
      !categories.includes(DocumentCategory.ENROLLMENT)
    ) {
      this.typeCode = undefined
    }
  }

  private get rawAttachments(): Attachment[] {
    return this.resource.content.map((c) => c.attachment)
  }

  /**
   * Returns attachments with stable, deterministic IDs suitable for use as React keys.
   *
   * Medplum stores files in S3 and returns presigned URLs with rotating query parameters.
   * The websocket cache sync frequently refreshes these URLs, changing the query string
   * even though the underlying file hasn't changed. To produce a stable ID, we strip
   * query parameters from Medplum storage URLs before incorporating them into the ID.
   *
   * ID priority: `{docRefId}-{hash | strippedUrl | index}`
   * - `hash` is preferred (content-addressable, survives re-uploads to different URLs)
   * - `url` (stripped) is the fallback for attachments without a hash
   * - `index` is the last resort for attachments with neither hash nor URL
   */
  get attachments(): AttachmentWithId[] {
    return this.rawAttachments.map((attachment, index) => {
      let url = attachment.url
      if (url?.includes('https://storage.medplum.com')) {
        url = url.split('?')[0]
      }

      return {
        ...attachment,
        id: `${this.id ?? 'unsaved'}-${attachment.hash ?? url ?? index}`,
      }
    })
  }

  set attachments(attachments: Attachment[]) {
    this.resource.content = attachments.map((attachment) => ({ attachment }))
    this.transcriptionPages = undefined
    // An outcome describes the previous attachments, not the new ones.
    this.transcriptionOutcome = undefined
    this.transcriptionStartedAt = undefined
    this.transcriptionFailedAttempts = undefined
  }

  get attachmentContentHash(): string | undefined {
    return this.getTag(ATTACHMENT_CONTENT_HASH_SYSTEM)?.code
  }

  set attachmentContentHash(hash: string | undefined) {
    this.removeTag({ system: ATTACHMENT_CONTENT_HASH_SYSTEM })
    if (hash && hash.length > 0) {
      this.addTag({
        system: ATTACHMENT_CONTENT_HASH_SYSTEM,
        code: hash,
      })
    }
  }

  setAttachmentTitleAtIndex(index: number, title: string): void {
    const attachment = this.rawAttachments[index]
    if (!attachment) {
      return
    }
    attachment.title = title.trim()
  }

  /**
   * Flips the `attachment-conversion` extension at a given index back to
   * `pending`. Designed for the "retry conversion" affordance on failed-state
   * placeholders: re-arming the extension causes the Medplum subscription to
   * dispatch and the conversion handler to run again.
   *
   * No-op when the index is out of range.
   *
   * @param index - Index into the raw `content` array.
   * @param startedAt - ISO timestamp to record on the new pending state.
   *   Defaults to `now()`.
   */
  retryAttachmentConversionAtIndex(index: number, startedAt?: string): void {
    const attachment = this.rawAttachments[index]
    if (!attachment) {
      return
    }
    const next = setAttachmentConversionState(attachment, {
      status: 'pending',
      startedAt: startedAt ?? new Date().toISOString(),
    })
    // Why: assign back to the same `content[i].attachment` slot so the
    // mutation proxy diffs against the existing attachment and emits
    // minimal JSON Patch ops scoped to the extension array.
    this.resource.content[index].attachment = next
  }

  get author(): Reference<Practitioner> | Reference<Organization> | undefined {
    return this.resource.author?.[0] as
      | Reference<Practitioner>
      | Reference<Organization>
      | undefined
  }

  get authorPractitioner(): Reference<Practitioner> | undefined {
    const author = this.author
    return isOfType(author, 'Practitioner') ? author : undefined
  }

  get date(): Date {
    return new Date(this.resource.date ?? this.resource.meta?.lastUpdated ?? '')
  }

  get dateString(): string | undefined {
    return this.resource.date
  }

  set dateString(value: string | undefined) {
    if (value) {
      this.resource.date = value
    } else {
      delete this.resource.date
    }
  }

  get dateStringWithFallback(): string | undefined {
    return this.resource.date ?? this.resource.meta?.lastUpdated
  }

  get timeSinceUploaded(): string {
    return this.resource.date
      ? formatDistance(new Date(this.date), new Date())
      : ''
  }

  /* The clinical date of service — the date the document itself is about
   * (visit/collection/study/order date), as opposed to `date` which is the
   * upload/ingestion timestamp. Stored in the FHIR-native `context.period`.
   * Undefined when no service date could be determined. */
  get serviceDateString(): string | undefined {
    return this.resource.context?.period?.start
  }

  set serviceDateString(value: string | undefined) {
    if (!value) {
      if (this.resource.context?.period) {
        delete this.resource.context.period
      }
      return
    }
    this.resource.context ??= {}
    this.resource.context.period = { start: value, end: value }
  }

  /* DateString form of the service date, for date pickers / editing. */
  get serviceDate(): DateString | null {
    const start = this.resource.context?.period?.start
    return start ? toDateString(start) : null
  }

  set serviceDate(value: DateString | null) {
    this.serviceDateString = value ?? undefined
  }

  get subject(): Reference<Patient> | undefined {
    return this.resource.subject as Reference<Patient> | undefined
  }

  set subject(subject: Reference<Patient> | undefined) {
    this.resource.subject = subject
  }

  private getRelatedReference<T extends ResourceType>(
    type: T
  ): Reference<ExtractResource<T>> | undefined {
    return this.resource.context?.related?.find((r) => isOfType(r, type))
  }
  private setRelatedReference<T extends ResourceType>(
    type: T,
    reference: Reference<ExtractResource<T>> | undefined
  ): void {
    const related =
      this.resource.context?.related?.filter((r) => !isOfType(r, type)) ?? []
    if (reference) {
      related.push(reference)
    }
    this.resource.context ??= {}
    // FHIR rejects an empty array, so drop the field when nothing is left.
    if (related.length) {
      this.resource.context.related = related
    } else {
      delete this.resource.context.related
    }
  }

  private getRelatedReferences<T extends ResourceType>(
    type: T
  ): Reference<ExtractResource<T>>[] | undefined {
    return this.resource.context?.related?.filter((r) => isOfType(r, type))
  }
  private setRelatedReferences<T extends ResourceType>(
    type: T,
    references: Reference<ExtractResource<T>>[] | undefined
  ): void {
    const related =
      this.resource.context?.related?.filter((r) => !isOfType(r, type)) ?? []
    if (references?.length) {
      related.push(...references)
    }
    this.resource.context ??= {}
    // FHIR rejects an empty array, so drop the field when nothing is left.
    if (related.length) {
      this.resource.context.related = uniqBy(related, (r) =>
        getReferenceString(r)
      )
    } else {
      delete this.resource.context.related
    }
  }

  get related(): Reference[] | undefined {
    return this.resource.context?.related
  }

  /**
   * Documents need manual review when they have no patient (subject) and no
   * linked orders/encounters (context.related). Health Gorilla lab-result
   * DocumentReferences are excluded because they are always linked to a lab
   * order via the Observation→DiagnosticReport→ServiceRequest chain, and a
   * ReviewLabResultsTask is created for the order instead. Inline-paste
   * documents are excluded because they are already embedded in a note/task
   * body; a separate review task double-tasks the author.
   */
  get shouldBeReviewed(): boolean {
    if (this.resource.meta?.source === HEALTH_GORILLA_META_SOURCE) {
      return false
    }
    if (this.source === DocumentSource.INLINE_PASTE) {
      return false
    }
    return !this.subject && !this.related?.length
  }

  get orders(): Reference<ServiceRequest>[] | undefined {
    return this.getRelatedReferences('ServiceRequest')
  }

  set orders(orders: Reference<ServiceRequest>[] | undefined) {
    this.setRelatedReferences('ServiceRequest', orders)
  }

  get questAccessions(): string[] {
    return compact(
      this.identifiers(QUEST_ACCESSION_IDENTIFIER_SYSTEM).map((id) => id.value)
    )
  }

  set questAccessions(accessions: string[]) {
    const current = this.questAccessions
    for (const stale of current.filter(
      (value) => !accessions.includes(value)
    )) {
      this.removeIdentifier(QUEST_ACCESSION_IDENTIFIER_SYSTEM, stale)
    }
    for (const added of accessions.filter(
      (value) => !current.includes(value)
    )) {
      this.addIdentifier(QUEST_ACCESSION_IDENTIFIER_SYSTEM, added)
    }
  }

  get labCopyGroup(): string | undefined {
    return this.getTag(DOCUMENT_LAB_COPY_GROUP_SYSTEM)?.code
  }

  // Reference string of the primary copy this document is a copy of.
  get labCopyOf(): string | undefined {
    return this.getTag(DOCUMENT_LAB_COPY_OF_SYSTEM)?.code
  }

  get isLabCopySecondary(): boolean {
    return this.labCopyOf !== undefined
  }

  get labCopyState(): LabCopyState {
    return {
      group: this.labCopyGroup,
      copyOf: this.labCopyOf,
      pinned: this.getTag(DOCUMENT_LAB_COPY_PINNED_SYSTEM) !== undefined,
    }
  }

  get labCopyGroupParticipant(): string | undefined {
    return this.getTag(DOCUMENT_LAB_COPY_PARTICIPANT_SYSTEM)?.code
  }

  get folderId(): string | undefined {
    return this.tags.find((tag) => tag.system === FOLDER_ID_SYSTEM)?.code
  }

  get isFiledByLabCopyReconciler(): boolean {
    return this.getTag(DOCUMENT_LAB_COPY_FILED_SYSTEM) !== undefined
  }

  markFiledByLabCopyReconciler(): void {
    this.setTag(DOCUMENT_LAB_COPY_FILED_SYSTEM, 'filed')
  }

  // `participant` is the reference string of the participant the group is
  // formed under; it is recorded only while the document is in a group.
  setLabCopyState(
    { group, copyOf, pinned }: LabCopyState,
    participant: string
  ): void {
    if (group) {
      this.setTag(DOCUMENT_LAB_COPY_GROUP_SYSTEM, group)
      this.setTag(DOCUMENT_LAB_COPY_PARTICIPANT_SYSTEM, participant)
    } else {
      this.removeTag({ system: DOCUMENT_LAB_COPY_GROUP_SYSTEM })
      this.removeTag({ system: DOCUMENT_LAB_COPY_PARTICIPANT_SYSTEM })
    }
    if (copyOf) {
      this.setTag(DOCUMENT_LAB_COPY_OF_SYSTEM, copyOf)
    } else {
      this.removeTag({ system: DOCUMENT_LAB_COPY_OF_SYSTEM })
    }
    if (pinned) {
      this.setTag(DOCUMENT_LAB_COPY_PINNED_SYSTEM, 'open-review')
    } else {
      this.removeTag({ system: DOCUMENT_LAB_COPY_PINNED_SYSTEM })
    }
  }

  // Health Gorilla's id for the report a Quest PDF or report rendering came
  // from: the whole identifier value on the rendering, and the prefix before the
  // file name on a PDF. Requisitions and labels carry their order's id instead,
  // so they have none.
  get healthGorillaReportId(): string | undefined {
    const kind = this.labCopyKind
    if (
      kind !== LabCopyKind.QUEST_PDF &&
      kind !== LabCopyKind.HEALTH_GORILLA_REPORT
    ) {
      return undefined
    }
    for (const { value } of this.identifiers(
      HEALTH_GORILLA_IDENTIFIER_SYSTEM
    )) {
      const reportId = value?.match(HEALTH_GORILLA_DOCUMENT_ID)?.[1]
      if (reportId) {
        return reportId
      }
    }
    return undefined
  }

  // Which kind of lab result copy this is, or undefined for anything else:
  // requisitions and specimen labels (typed by Health Gorilla's document type
  // category) never carry a result. Quest's PDFs are Health Gorilla attachments
  // named `<report id>-<file name>.pdf` (verified against the PDF metadata:
  // Creator "Quest Diagnostics Reporting Services"); the report rendering is the
  // typed document whose identifier is the bare report id (Producer "OpenPDF",
  // Health Gorilla's own).
  get labCopyKind(): LabCopyKind | undefined {
    if (this.status !== 'current') {
      return undefined
    }
    const healthGorillaTypes = (this.resource.category ?? [])
      .flatMap((category) => category.coding ?? [])
      .filter((coding) => coding.system === HEALTH_GORILLA_DOCUMENT_TYPE_SYSTEM)
      .map((coding) => coding.code)
    if (healthGorillaTypes.some((code) => code !== 'DiagnosticReport')) {
      return undefined
    }
    for (const { value } of this.identifiers(
      HEALTH_GORILLA_IDENTIFIER_SYSTEM
    )) {
      if (value && HEALTH_GORILLA_PDF_ID.test(value)) {
        return LabCopyKind.QUEST_PDF
      }
      if (
        value &&
        HEALTH_GORILLA_REPORT_ID.test(value) &&
        healthGorillaTypes.includes('DiagnosticReport')
      ) {
        return LabCopyKind.HEALTH_GORILLA_REPORT
      }
    }
    return this.elationLabAccession ? LabCopyKind.ELATION : undefined
  }

  // The one Quest accession an Elation report saved. A report spanning several
  // accessions belongs to several results at once, so it joins no group.
  get elationLabAccession(): string | undefined {
    if (this.identifiers(ELATION_SANDBOX_ID_SYSTEM).length === 0) {
      return undefined
    }
    // A value that is not accession-shaped (a Lab Reference ID, say) says
    // nothing about which result this is, so it does not count as a second one.
    const accessions = new Set(
      compact(this.questAccessions.map((value) => parseQuestAccession(value)))
    )
    const [accession] = accessions
    return accessions.size === 1 ? accession : undefined
  }

  get coverage(): Reference<Coverage> | undefined {
    return this.getRelatedReference('Coverage')
  }

  set coverage(coverage: Reference<Coverage> | undefined) {
    this.setRelatedReference('Coverage', coverage)
  }

  get context(): string | undefined {
    return (
      codeFromCodeableConcept(this.resource.context?.practiceSetting) ??
      undefined
    )
  }

  set typeCode(type: DocumentTypes | undefined) {
    this.resource.type = type ? DOCUMENT_TYPES[type] : undefined
    const hasEnrollmentCategory = this.categories.includes(
      DocumentCategory.ENROLLMENT
    )
    invariant(
      type === DocumentTypes.MEDICAID_ID || type === DocumentTypes.MEDICARE_ID
        ? hasEnrollmentCategory
        : true,
      'MEDICAID and MEDICARE IDS must be marked as ENROLLMENT category'
    )
  }

  get typeCode(): DocumentTypes | undefined {
    return codeFromCodeableConcept(this.resource.type) as
      | DocumentTypes
      | undefined
  }

  get status(): DocumentReference['status'] {
    return this.resource.status
  }

  set status(status: DocumentReference['status']) {
    this.resource.status = status
  }

  private get docStatus(): DocumentReference['docStatus'] {
    return this.resource.docStatus ?? 'preliminary'
  }

  private set docStatus(docStatus: DocumentReference['docStatus']) {
    this.resource.docStatus = docStatus
  }

  get isConfirmed(): boolean {
    return this.docStatus === 'final'
  }

  markAsConfirmed(): void {
    this.docStatus = 'final'
    if (this.status === 'entered-in-error') {
      this.status = 'current'
    }
  }

  markDeleted(): void {
    this.resource.status = 'entered-in-error'
  }

  set folder(folder: Folder | undefined) {
    if (folder) {
      this.addTag({
        system: FOLDER_ID_SYSTEM,
        code: folder.id,
        display: folder.name,
      })
    } else {
      this.removeTag({ system: FOLDER_ID_SYSTEM })
    }
  }

  get folder(): Folder | undefined {
    const folderId = this.tags.find((t) => t.system === FOLDER_ID_SYSTEM)?.code
    if (!folderId) {
      return undefined
    }
    return FOLDERS.find((f) => f.id === folderId)
  }

  addMagicLinkToken(
    reference: Reference,
    token: string,
    expires: string
  ): void {
    this.resource.extension ??= []
    this.resource.extension.push({
      // May need to segment the top-level extension url based on the related reference, if the same document is used in multiple places with magic links
      // so that each can have its own magic link, token, and expiration
      url: DOCUMENT_REFERENCE_ATTACHMENT_MAGIC_LINK_EXTENSION,
      extension: [
        {
          url: 'token',
          valueString: token,
        },
        {
          url: 'reference',
          valueReference: reference,
        },
        {
          url: 'expires',
          valueString: expires,
        },
      ],
    })
  }

  isValidMagicLinkToken(token: string): boolean {
    const extension = this.resource.extension?.find(
      (e) =>
        e.url === DOCUMENT_REFERENCE_ATTACHMENT_MAGIC_LINK_EXTENSION &&
        e.extension?.some((e) => e.url === 'token' && e.valueString === token)
    )
    if (!extension) {
      return false
    }
    const expires = extension.extension?.find(
      (e) => e.url === 'expires' && e.valueString
    )
    if (!expires?.valueString) {
      return false
    }

    return isBefore(now(), expires.valueString)
  }

  @Extension({
    url: DOCUMENT_REFERENCE_TRANSCRIPTION_SYSTEM,
    kind: 'valueString',
  })
  transcription?: string

  @Extension({
    url: DOCUMENT_REFERENCE_TRANSCRIPTION_PAGES_SYSTEM,
    kind: 'valueString',
  })
  private _transcriptionPagesJson?: string

  get transcriptionPages(): DocumentTranscriptionPageSpan[] | undefined {
    if (!this._transcriptionPagesJson) {
      return undefined
    }
    try {
      const spans = transcriptionPageSpansFrom(
        JSON.parse(this._transcriptionPagesJson) as JsonLike
      )
      return spans?.every(
        (span) =>
          this.transcription !== undefined &&
          span.attachmentIndex < this.attachments.length &&
          span.end <= this.transcription.length
      )
        ? spans
        : undefined
    } catch {
      return undefined
    }
  }

  set transcriptionPages(value: DocumentTranscriptionPageSpan[] | undefined) {
    if (!value?.length) {
      this._transcriptionPagesJson = undefined
      return
    }
    const spans = transcriptionPageSpansFrom(value)
    invariant(spans, 'Invalid document transcription page spans')
    invariant(
      spans.every((span) => span.attachmentIndex < this.attachments.length),
      'Document transcription page span references a missing attachment'
    )
    const transcriptionLength = this.transcription?.length
    invariant(
      transcriptionLength !== undefined &&
        spans.every((span) => span.end <= transcriptionLength),
      'Document transcription page spans exceed the transcription'
    )
    this._transcriptionPagesJson = JSON.stringify(spans)
  }

  transcriptionPageForOffset(
    offset: number
  ):
    | Pick<DocumentTranscriptionPageSpan, 'attachmentIndex' | 'page'>
    | undefined {
    if (!isNonNegativeInteger(offset)) {
      return undefined
    }
    const span = this.transcriptionPages?.find(
      (candidate) => candidate.start <= offset && offset < candidate.end
    )
    return span
      ? { attachmentIndex: span.attachmentIndex, page: span.page }
      : undefined
  }

  transcriptionTextForPage(
    attachmentIndex: number,
    page: number
  ): string | undefined {
    const span = this.transcriptionPages?.find(
      (candidate) =>
        candidate.attachmentIndex === attachmentIndex && candidate.page === page
    )
    return span && this.transcription !== undefined
      ? this.transcription.slice(span.start, span.end)
      : undefined
  }

  // Extractor's self-reported confidence in `transcription`, 0-100. Low values
  // and handwriting flag the documents whose transcription cannot be trusted
  // as text (checkbox/handwriting-bearing forms lose the marks silently).
  @Extension({
    url: DOCUMENT_REFERENCE_TRANSCRIPTION_CONFIDENCE_SYSTEM,
    kind: 'valueDecimal',
  })
  transcriptionConfidence?: number

  @Extension({
    url: DOCUMENT_REFERENCE_CONTAINS_HANDWRITING_SYSTEM,
    kind: 'valueBoolean',
  })
  containsHandwriting?: boolean

  @Extension({
    url: DOCUMENT_REFERENCE_AI_SUMMARY_SYSTEM,
    kind: 'valueString',
  })
  aiSummary?: string

  // TODO: rename / replace EXTERNAL_FILE_ID_SYSTEM with TWENTY_ATTACHMENT_ID_SYSTEM
  get twentyAttachmentId(): string | undefined {
    return this.identifier(EXTERNAL_FILE_ID_SYSTEM)?.value
  }

  set twentyAttachmentId(value: string | undefined) {
    if (value) {
      this.setIdentifier(EXTERNAL_FILE_ID_SYSTEM, value)
    } else {
      this.removeIdentifier(EXTERNAL_FILE_ID_SYSTEM)
    }
  }

  static byTwentyAttachmentId(attachmentId: string): QueryTypes {
    return {
      identifier: `${EXTERNAL_FILE_ID_SYSTEM}|${attachmentId}`,
    }
  }

  get fingerprint(): string | undefined {
    return this.getTag(DOCUMENT_FINGERPRINT_SYSTEM)?.code
  }

  set fingerprint(value: string | undefined) {
    this.setTagsBySystem(
      DOCUMENT_FINGERPRINT_SYSTEM,
      value ? [{ system: DOCUMENT_FINGERPRINT_SYSTEM, code: value }] : []
    )
  }

  // Every status: a re-delivery of a document a user removed is still the
  // same delivery and must not bring it back.
  static byDeliveryIdentifier(
    delivery: Required<Pick<Identifier, 'system' | 'value'>>
  ): QueryTypes {
    return { identifier: `${delivery.system}|${delivery.value}` }
  }

  // Current documents visible on the participant's chart only: a removed or
  // hidden copy (AI chat upload, internal) must not swallow a new arrival.
  static byParticipantFingerprint(
    participant: Reference<Patient>,
    fingerprint: string
  ): QueryTypes {
    return {
      ...this.byParticipant({ participant }),
      status: 'current',
      _tag: `${DOCUMENT_FINGERPRINT_SYSTEM}|${fingerprint}`,
    }
  }

  // Folds a duplicate arrival into this document instead of saving it. Adds
  // what identifies or links the arrival (source identifiers, care links,
  // categories, authors) and keeps everything a reviewer may have worked on:
  // subject, content, title, dates, status, tags, transcription.
  absorbDuplicateArrival(arrival: ExtendedDocumentReference): void {
    for (const id of arrival.identifiers()) {
      if (id.system && id.value) {
        this.addIdentifierIfMissing(id)
      }
    }

    this.unionCareLinks(arrival)

    const merged = normalizeDocumentCategories([
      ...this.categories,
      ...arrival.categories,
    ])
    if (merged.join() !== this.categories.join()) {
      this.categories = merged
    }

    const authors = uniqBy(
      [...(this.resource.author ?? []), ...(arrival.resource.author ?? [])],
      (reference) => reference.reference ?? JSON.stringify(reference)
    )
    if (authors.length > (this.resource.author?.length ?? 0)) {
      this.resource.author = authors
    }
  }

  get isIntakeManaged(): boolean {
    return this.getTag(DOCUMENT_INTAKE_SYSTEM)?.code === DOCUMENT_INTAKE_MANAGED
  }

  markIntakeManaged(): void {
    this.setTagsBySystem(DOCUMENT_INTAKE_SYSTEM, [
      { system: DOCUMENT_INTAKE_SYSTEM, code: DOCUMENT_INTAKE_MANAGED },
    ])
  }

  private unionCareLinks(other: ExtendedDocumentReference): void {
    const referenceKey = (reference: Reference) =>
      reference.reference ?? JSON.stringify(reference)
    const related = uniqBy(
      [...(this.related ?? []), ...(other.related ?? [])],
      referenceKey
    )
    const encounters = uniqBy(
      [
        ...(this.resource.context?.encounter ?? []),
        ...(other.resource.context?.encounter ?? []),
      ],
      referenceKey
    )
    if (related.length > (this.related?.length ?? 0)) {
      this.resource.context = { ...this.resource.context, related }
    }
    if (encounters.length > (this.resource.context?.encounter?.length ?? 0)) {
      this.resource.context = {
        ...this.resource.context,
        encounter: encounters,
      }
    }
  }

  private addIdentifierIfMissing(identifier: Identifier): void {
    const exists = this.identifiers(identifier.system).some(
      (id) => id.value === identifier.value
    )
    if (!exists) {
      this.resource.identifier = [
        ...(this.resource.identifier ?? []),
        identifier,
      ]
    }
  }

  static faxInboxQuery(options?: { missingSubject?: boolean }): QueryTypes {
    const query: QueryTypes = { ...this.searchQuery(), _count: 20 }
    if (options?.missingSubject) {
      query['subject:missing'] = true
    }
    return query
  }

  set predictedRelations(
    relations: {
      reference: string
      confidence: number
      dismissed?: boolean | null
      reason?: string | null
    }[]
  ) {
    this.removeTag({ system: DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM })

    if (relations.length === 0) {
      // Same system as real hits so `_tag:not system|` excludes the
      // document after an empty run. Not a resource reference, so the
      // getter stays [].
      this.addTag({
        system: DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM,
        code: DOCUMENT_REFERENCE_PREDICTED_RELATIONS_NONE_CODE,
      })
      return
    }

    for (const { reference, confidence, dismissed, reason } of relations) {
      const data: PredictedRelationData = { confidence }
      if (dismissed) {
        data.dismissed = dismissed
      }
      if (reason) {
        data.reason = reason
      }

      this.addTag({
        system: DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM,
        code: reference,
        extension: [
          {
            url: DOCUMENT_REFERENCE_PREDICTED_RELATIONS_EXTENSION,
            valueString: JSON.stringify(data),
          },
        ],
      })
    }
  }

  // Counts the failure against the attempt budget.
  markPredictedRelationsFailed(): void {
    this.removeTag({ system: DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM })
    this.addTag({
      system: DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM,
      code: DOCUMENT_REFERENCE_PREDICTED_RELATIONS_FAILED_CODE,
    })
    this.predictionFailedAttempts = nextFailedAttemptCount(
      this.predictionFailedAttempts
    )
  }

  get predictionFailedAttempts(): number | undefined {
    return parseFailedAttemptCount(
      this.getTag(DOCUMENT_REFERENCE_PREDICTION_FAILED_ATTEMPTS_SYSTEM)?.code
    )
  }

  set predictionFailedAttempts(count: number | undefined) {
    this.setTagsBySystem(
      DOCUMENT_REFERENCE_PREDICTION_FAILED_ATTEMPTS_SYSTEM,
      count ? [{ code: String(count) }] : []
    )
  }

  get hasPredictionAttemptBudget(): boolean {
    return hasAttemptBudget(this.predictionFailedAttempts)
  }

  get hasFailedPredictedRelationsAttempt(): boolean {
    return this.tags.some(
      (tag) =>
        tag.system === DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM &&
        tag.code === DOCUMENT_REFERENCE_PREDICTED_RELATIONS_FAILED_CODE
    )
  }

  // Also drops the started marker: a deliberate re-run must not be read as an
  // abandoned attempt, and the failed-attempt count so the document gets a
  // fresh budget. The automatic retry sweep passes `keepFailedAttempts` so the
  // retry that fails again is final; a failure recorded before attempts were
  // counted has no count, so the kept count is at least 1.
  clearFailedPredictedRelationsAttempt(
    options: { keepFailedAttempts?: boolean } = {}
  ): void {
    this.removeTag({
      system: DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM,
      code: DOCUMENT_REFERENCE_PREDICTED_RELATIONS_FAILED_CODE,
    })
    this.predictionStartedAt = undefined
    this.predictionFailedAttempts = options.keepFailedAttempts
      ? (this.predictionFailedAttempts ?? 1)
      : undefined
  }

  get predictionStartedAt(): string | undefined {
    return this.getTag(DOCUMENT_REFERENCE_PREDICTION_STARTED_SYSTEM)?.code
  }

  set predictionStartedAt(startedAt: string | undefined) {
    this.setTagsBySystem(
      DOCUMENT_REFERENCE_PREDICTION_STARTED_SYSTEM,
      startedAt ? [{ code: startedAt }] : []
    )
  }

  get hasPredictedRelationsAttempt(): boolean {
    return this.tags.some(
      (tag) => tag.system === DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM
    )
  }

  get predictedRelations(): {
    reference: Reference
    confidence: number
    reason?: string
  }[] {
    return compact(
      this.tags
        .filter(
          (t) => t.system === DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM
        )
        .map((tag) => {
          const result = this.getPredictedRelationTagData(tag)

          if (!result || result.data.dismissed) {
            return null
          }
          const reference = parseReferenceString(tag.code)
          if (!reference) {
            return null
          }

          return {
            reference,
            confidence: result.data.confidence,
            reason: result.data.reason,
          }
        })
    )
  }

  get predictedSubject():
    | { reference: Reference<Patient>; confidence: number; reason?: string }
    | undefined {
    if (this.resource.subject) {
      return undefined
    }
    return (this.predictedRelations ?? []).find(
      (r) => r.reference && isOfType(r.reference, 'Patient')
    ) as
      | { reference: Reference<Patient>; confidence: number; reason?: string }
      | undefined
  }

  get predictedSubjects(): {
    reference: Reference<Patient>
    confidence: number
    reason?: string
  }[] {
    if (this.resource.subject) {
      return []
    }
    return orderBy(
      (this.predictedRelations ?? []).filter(
        (r) => r.reference && isOfType(r.reference, 'Patient')
      ),
      (relation) => relation.confidence ?? 0,
      ['desc']
    ) as {
      reference: Reference<Patient>
      confidence: number
      reason?: string
    }[]
  }

  get hasMultiplePredictedSubjects(): boolean {
    return this.predictedSubjects.length > 1
  }

  get predictedOrders():
    | {
        reference: Reference<ServiceRequest>
        confidence: number
        reason?: string
      }[]
    | undefined {
    const predicted = orderBy(
      (this.predictedRelations ?? []).filter(
        (r) => r.reference && isOfType(r.reference, 'ServiceRequest')
      ),
      (relation) => relation.confidence ?? 0,
      ['desc']
    ) as {
      reference: Reference<ServiceRequest>
      confidence: number
      reason?: string
    }[]
    return predicted.length > 0 ? predicted : undefined
  }

  private getPredictedRelationTagData(
    tag: Coding
  ): { data: PredictedRelationData; extension?: FHIRExtension } | null {
    const extension = tag.extension?.find(
      (e) => e.url === DOCUMENT_REFERENCE_PREDICTED_RELATIONS_EXTENSION
    )
    if (!extension?.valueString) {
      return null
    }
    try {
      const data: PredictedRelationData = JSON.parse(extension.valueString)
      return { data, extension }
    } catch {
      return null
    }
  }

  private updatePredictedRelationData(
    reference: string,
    updateFn: (data: PredictedRelationData) => void
  ): void {
    const tag = this.tags.find(
      (t) =>
        t.system === DOCUMENT_REFERENCE_PREDICTED_RELATION_SYSTEM &&
        t.code === reference
    )

    if (!tag) {
      return
    }

    const result = this.getPredictedRelationTagData(tag)
    if (!result) {
      return
    }

    updateFn(result.data)
  }

  dismissPredictedRelation(reference: Reference): void {
    const refString = getReferenceString(reference)
    this.updatePredictedRelationData(refString, (data) => {
      data.dismissed = true
    })
  }

  confirmPredictedOrder(reference: Reference<ServiceRequest>): void {
    const currentOrders = this.orders ?? []
    this.orders = [...currentOrders, reference]

    this.dismissPredictedRelation(reference)
  }

  dismissPredictedOrder(reference: Reference<ServiceRequest>): void {
    this.dismissPredictedRelation(reference)
  }

  /**
   * Check if a specific practitioner has signed this document
   * @param signatures - Array of Signature (Provenance) resources
   * @param practitioner - Practitioner reference to check
   * @returns true if practitioner has non-voided signature
   */
  static hasPractitionerSigned(
    signatures: { who: Reference<Practitioner>; voided: boolean }[],
    practitioner: Reference<Practitioner>
  ): boolean {
    const practitionerRef = getReferenceString(practitioner)
    return signatures.some(
      (sig) => getReferenceString(sig.who) === practitionerRef && !sig.voided
    )
  }

  protected getExportFields() {
    return {
      id: this.id,
      description: this.description,
      category: this.categories,
      status: this.status,
      date: this.dateString,
      timeSinceUploaded: this.timeSinceUploaded,
      authorId: resolveId(this.author),
      patientId: resolveId(this.subject),
      attachments: this.attachments.map((attachment) => ({
        contentType: attachment.contentType,
        title: attachment.title,
        size: attachment.size,
        url: attachment.url,
      })),
      type: this.typeCode,
      context: this.context,
      transcription: this.transcription,
      aiSummary: this.aiSummary,
      folder: this.folder,
    }
  }
}
